/**
 * The AI copilot — "talk to the HRMS".
 *
 * A conversation is a private, per-person thread. Each `sendMessage` runs one model TURN: the
 * model may call tools derived from this very contract (reads execute live, as the caller, over
 * the real routes), and a WRITE never executes inside the turn — it becomes a PROPOSAL the person
 * approves or rejects explicitly. `approveProposal` then calls the underlying route as the caller,
 * so `authenticate`, the route's own `can()`, RLS, idempotency and audit all run exactly as for a
 * hand-made request; the audit row carries `actor_type = 'ai'`.
 *
 * STREAMING: `sendMessage` answers JSON by default. With `Accept: text/event-stream` the same
 * route streams `aiSseEvent`s (`turn`, `delta`, `tool`, `proposal`, `navigate`, `done`, `error`)
 * and ends with `done`. A client that loses the stream calls `getTurn` to catch up — the turn keeps
 * running server-side.
 *
 * ATTACHMENTS: `POST /ai/attachments` is a plain multipart Express route (multer), not a ts-rest
 * one — the same shape as the careers resume upload — returning `aiAttachmentSchema`. Accepts
 * PDF, images (PNG/JPEG/WebP, read by OCR), CSV/XLSX spreadsheets and DOCX (`AI_ATTACHMENT_MIME`).
 * Text is extracted server-side at upload; the model never receives bytes.
 *
 * ALLOWANCES: every org has a per-period token allowance (from its plan) plus purchased add-on
 * packs. `getAllowance` is what the dock's meter reads; `AI_ALLOWANCE_EXHAUSTED` (429) is what
 * `sendMessage` answers when it is spent.
 *
 * Permissions: `ai.copilot:use` to talk; `ai.agent.write:execute` to have proposals made and to
 * approve them; `ai.settings:manage` / `org.billing:*` for allowances and usage.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { AI_EFFORTS } from '../enums.js';
import {
  errorBodySchema,
  instantSchema,
  paginatedSchema,
  paginationQuerySchema,
  uuidSchema,
} from '../schemas/common.js';
import { routeMeta } from './meta.js';

const c = initContract();

// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------

export const aiEffortSchema = z.enum(AI_EFFORTS);

export const AI_MESSAGE_ROLES = ['user', 'assistant', 'tool'] as const;
export const aiMessageRoleSchema = z.enum(AI_MESSAGE_ROLES);
export type AiMessageRole = z.infer<typeof aiMessageRoleSchema>;

export const AI_TURN_STATUSES = ['running', 'done', 'error'] as const;
export const aiTurnStatusSchema = z.enum(AI_TURN_STATUSES);
export type AiTurnStatus = z.infer<typeof aiTurnStatusSchema>;

export const AI_PROPOSAL_STATUSES = [
  'pending',
  'executing',
  'executed',
  'failed',
  'rejected',
  'abandoned',
  'expired',
] as const;
export const aiProposalStatusSchema = z.enum(AI_PROPOSAL_STATUSES);
export type AiProposalStatus = z.infer<typeof aiProposalStatusSchema>;

export const AI_TOOL_CALL_STATUSES = ['running', 'done', 'error'] as const;
export const aiToolCallStatusSchema = z.enum(AI_TOOL_CALL_STATUSES);

// ---------------------------------------------------------------------------
// Navigation — the copilot never emits a free-form path. It names a screen from this list and
// the client maps it to its own route (web and mobile differ); the query is validated per target.
// ---------------------------------------------------------------------------

export const AI_NAVIGATION_TARGETS = [
  'home',
  'employees.list',
  'employees.detail',
  'leave.requests',
  'leave.apply',
  'leave.calendar',
  'leave.approvals',
  'attendance.today',
  'attendance.history',
  'expenses.claims',
  'goals.list',
  'notifications',
  'profile',
  // Training (LMS): the admin course library, one course, the create-course wizard, live sessions,
  // reports and settings — and the learner's own My learning and course page.
  'training.courses',
  'training.course',
  'training.newCourse',
  'training.live',
  'training.reports',
  'training.settings',
  'learning.home',
  'learning.course',
  // Payroll (admin): overview, runs and one run's review, inputs, set-up, people, operations, compliance — and the
  // employee's own pay (payslips, payments). Settings > Roles is where payroll access is given.
  'payroll.overview',
  'payroll.runs',
  'payroll.run',
  'payroll.inputs',
  'payroll.salaryConfiguration',
  'payroll.statutory',
  'payroll.calendar',
  'payroll.profiles',
  'payroll.loans',
  'payroll.taxDeclarations',
  'payroll.payments',
  'payroll.fnf',
  'payroll.compliance',
  'payroll.reports',
  'payroll.queries',
  'payroll.myPay',
  'settings.roles',
] as const;
export const aiNavigationTargetSchema = z.enum(AI_NAVIGATION_TARGETS);
export type AiNavigationTarget = z.infer<typeof aiNavigationTargetSchema>;

/** Query values are always strings — they go straight into a URL. */
export const aiNavigationSchema = z.object({
  target: aiNavigationTargetSchema,
  query: z.record(z.string().max(200)).default({}),
  /** What the button says, e.g. "Open engineers on probation". */
  label: z.string().max(120).optional(),
});
export type AiNavigation = z.infer<typeof aiNavigationSchema>;

/**
 * Where to send someone to see what a write actually did.
 *
 * Keyed by the tool's MODULE, not by the tool: `leave__createRequest`, `leave__approveRequest` and
 * `leave__cancelRequest` all end up on the same screen, and a module the team adds tomorrow simply
 * has no entry and shows no link — which is the right failure. Ids go in the query where the
 * destination reads one; each app maps the target to its own route (`employees.detail` is a real
 * screen on mobile and the filtered directory on web), so a link is never more precise than the
 * app can honour.
 */
const OUTCOME_TARGET_BY_MODULE: Readonly<Record<string, AiNavigationTarget>> = {
  employees: 'employees.detail',
  onboarding: 'employees.detail',
  personalDetails: 'profile',
  leave: 'leave.requests',
  expense: 'expenses.claims',
  goals: 'goals.list',
  objectives: 'goals.list',
  attendance: 'attendance.today',
  timesheets: 'attendance.history',
  notifications: 'notifications',
  // A course write returns the course (the new version's id after an edit that forked one), and
  // `training_draft_course` — a hand-written plan tool, no `__` — carries the created course's
  // body as its result, so both open that course.
  trainingTasks: 'training.course',
  training_draft_course: 'training.course',
  trainingAssignments: 'training.courses',
  trainingCategories: 'training.courses',
  trainingLiveSessions: 'training.live',
  trainingCertificateTemplates: 'training.settings',
  // Payroll: a run write (and `payroll_run_step`, the hand-written run-step tool) opens that run; the rest open the screen
  // that lists what was changed.
  payrollRuns: 'payroll.run',
  payrollLifecycle: 'payroll.run',
  payroll_run_step: 'payroll.run',
  payrollInputs: 'payroll.inputs',
  payrollCompensation: 'payroll.profiles',
  payrollCalendar: 'payroll.calendar',
  payrollSettings: 'payroll.statutory',
  payrollRules: 'payroll.salaryConfiguration',
  payrollLoans: 'payroll.loans',
  payrollEwa: 'payroll.loans',
  payrollTax: 'payroll.taxDeclarations',
  payrollPayments: 'payroll.payments',
  payrollPayslips: 'payroll.runs',
  payrollFnf: 'payroll.fnf',
  payrollCompliance: 'payroll.compliance',
  payrollReports: 'payroll.reports',
  payrollQueries: 'payroll.queries',
  roles: 'settings.roles',
};

/**
 * The id a destination wants, when it wants one: the query key it reads, and the response field
 * that holds it (they differ when the route answers with a plain `id`).
 */
const OUTCOME_QUERY_KEY: Partial<
  Record<AiNavigationTarget, { readonly key: string; readonly from: string | readonly string[] }>
> = {
  'employees.detail': { key: 'employmentId', from: 'employmentId' },
  'training.course': { key: 'courseId', from: 'id' },
  'training.live': { key: 'sessionId', from: 'id' },
  // A run detail answers with `id`; a queued calculation (202) and the lifecycle routes with `runId`.
  'payroll.run': { key: 'runId', from: ['runId', 'id'] },
};

/**
 * The screen that shows what an executed proposal produced, or `null` when this module has no
 * destination in the allow-list. `result` is the route's own response body.
 */
export function navigationForOutcome(tool: string, result: unknown): AiNavigation | null {
  const target = OUTCOME_TARGET_BY_MODULE[tool.split('__')[0] ?? ''];
  if (!target) return null;

  const query: Record<string, string> = {};
  const wanted = OUTCOME_QUERY_KEY[target];
  if (wanted && result && typeof result === 'object') {
    for (const field of typeof wanted.from === 'string' ? [wanted.from] : wanted.from) {
      const value = (result as Record<string, unknown>)[field];
      if (typeof value === 'string' && value.length > 0) {
        query[wanted.key] = value;
        break;
      }
    }
  }
  return { target, query };
}

// ---------------------------------------------------------------------------
// Blocks — the UI-facing shape of a message. What the model sees (chat-completions messages)
// is stored server-side; clients render blocks.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Questions — the copilot asking the person to pick, the way a form would, instead of in prose.
// The model calls `ask_user`; the turn ends with this block; the chosen option's `value` is sent
// back as the person's next message, so the model reads an answer, not a click.
// ---------------------------------------------------------------------------

export const aiQuestionOptionSchema = z.object({
  /** What the chip says. */
  label: z.string().trim().min(1).max(80),
  /** What is sent as the reply when tapped — usually the label, sometimes fuller ("Casual leave, full day"). */
  value: z.string().trim().min(1).max(200),
  description: z.string().trim().max(160).optional(),
});

export const aiQuestionSchema = z.object({
  id: z.string().min(1).max(64),
  prompt: z.string().trim().min(1).max(400),
  options: z.array(aiQuestionOptionSchema).min(2).max(6),
  /** Show an "Other…" affordance that focuses the composer. Default true. */
  allowFreeText: z.boolean().default(true),
  /** Several chips may be picked; the reply joins their values with ", ". */
  multiSelect: z.boolean().default(false),
});
export type AiQuestion = z.infer<typeof aiQuestionSchema>;
export type AiQuestionInput = z.input<typeof aiQuestionSchema>;

export const aiBlockSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('text'), text: z.string() }),
  z.object({
    type: z.literal('tool_call'),
    id: z.string(),
    name: z.string(),
    /** Human wording of the call, e.g. "Looked up your leave balance". */
    summary: z.string().max(200).optional(),
    status: aiToolCallStatusSchema,
  }),
  z.object({ type: z.literal('proposal'), proposalId: uuidSchema }),
  z.object({ type: z.literal('navigate'), navigation: aiNavigationSchema }),
  z.object({ type: z.literal('attachment'), attachmentId: uuidSchema, filename: z.string() }),
  z.object({ type: z.literal('question'), question: aiQuestionSchema }),
]);
export type AiBlock = z.infer<typeof aiBlockSchema>;

export const aiMessageSchema = z.object({
  id: uuidSchema,
  conversationId: uuidSchema,
  turnId: uuidSchema.nullable(),
  role: aiMessageRoleSchema,
  blocks: z.array(aiBlockSchema),
  createdAt: instantSchema,
});
export type AiMessage = z.infer<typeof aiMessageSchema>;

export const aiTurnSchema = z.object({
  id: uuidSchema,
  conversationId: uuidSchema,
  status: aiTurnStatusSchema,
  model: z.string(),
  effort: aiEffortSchema,
  promptTokens: z.number().int().nonnegative(),
  completionTokens: z.number().int().nonnegative(),
  cachedPromptTokens: z.number().int().nonnegative(),
  iterations: z.number().int().nonnegative(),
  latencyMs: z.number().int().nonnegative(),
  finishReason: z.string().nullable(),
  toolsCalled: z.array(z.string()),
  error: z.string().nullable(),
  startedAt: instantSchema,
  endedAt: instantSchema.nullable(),
});
export type AiTurn = z.infer<typeof aiTurnSchema>;

/** A label/value pair the proposal card shows — "Leave type: Casual leave", "Dates: 14 Oct". */
export const aiProposalFactSchema = z.object({
  label: z.string().max(80),
  value: z.string().max(400),
});

export const aiProposalSchema = z.object({
  id: uuidSchema,
  conversationId: uuidSchema,
  turnId: uuidSchema,
  /** The derived tool name, e.g. `leave__createRequest`. */
  tool: z.string(),
  /** One line for the card header, e.g. "File a leave request". */
  title: z.string().max(200),
  facts: z.array(aiProposalFactSchema),
  /** Exactly what the route will receive on approval: `{ params?, query?, body? }`. */
  input: z.record(z.unknown()),
  status: aiProposalStatusSchema,
  /** The route's response body once executed. */
  result: z.unknown().nullable(),
  error: z.string().nullable(),
  expiresAt: instantSchema,
  decidedAt: instantSchema.nullable(),
  createdAt: instantSchema,
});
export type AiProposal = z.infer<typeof aiProposalSchema>;

export const aiConversationSchema = z.object({
  id: uuidSchema,
  title: z.string().nullable(),
  effort: aiEffortSchema,
  model: z.string(),
  lastMessageAt: instantSchema.nullable(),
  createdAt: instantSchema,
});
export type AiConversation = z.infer<typeof aiConversationSchema>;

/**
 * What a copilot attachment may be. The API validates the multipart upload against this list and
 * the web file picker derives its `accept` from it. `.xls` / `.xlsm` are deliberately absent
 * (legacy binary / macro formats are rejected with a clear message).
 */
export const AI_ATTACHMENT_MIME = {
  pdf: 'application/pdf',
  png: 'image/png',
  jpeg: 'image/jpeg',
  webp: 'image/webp',
  csv: 'text/csv',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
} as const;

export const AI_ATTACHMENT_MIME_TYPES = Object.values(AI_ATTACHMENT_MIME) as readonly string[];

/** File extensions (lower-case, with dot) that map to the allowed mime types. */
export const AI_ATTACHMENT_EXTENSIONS = [
  '.pdf',
  '.png',
  '.jpg',
  '.jpeg',
  '.webp',
  '.csv',
  '.xlsx',
  '.docx',
] as const;

/** Per-file size cap, in bytes (10 MB). */
export const AI_ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;
/** Attachments per message — matches `sendMessageBodySchema.attachmentIds`. */
export const AI_ATTACHMENT_MAX_PER_MESSAGE = 3;
/** Spreadsheet parse limits. */
export const AI_SPREADSHEET_MAX_SHEETS = 5;
export const AI_SPREADSHEET_MAX_ROWS = 2000;
export const AI_SPREADSHEET_MAX_COLUMNS = 60;

export const AI_ATTACHMENT_KINDS = ['pdf', 'image', 'spreadsheet', 'document'] as const;
export const aiAttachmentKindSchema = z.enum(AI_ATTACHMENT_KINDS);
export type AiAttachmentKind = z.infer<typeof aiAttachmentKindSchema>;

/** The kind of an attachment from its mime type, or `null` when the type is not allowed. */
export function attachmentKindForMime(mime: string): AiAttachmentKind | null {
  const m = mime.split(';')[0]?.trim().toLowerCase() ?? '';
  switch (m) {
    case AI_ATTACHMENT_MIME.pdf:
      return 'pdf';
    case AI_ATTACHMENT_MIME.png:
    case AI_ATTACHMENT_MIME.jpeg:
    case AI_ATTACHMENT_MIME.webp:
      return 'image';
    case AI_ATTACHMENT_MIME.csv:
    case AI_ATTACHMENT_MIME.xlsx:
      return 'spreadsheet';
    case AI_ATTACHMENT_MIME.docx:
      return 'document';
    default:
      return null;
  }
}

export const aiAttachmentSchema = z.object({
  id: uuidSchema,
  filename: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number().int().nonnegative(),
  /**
   * What the file is. Optional so rows created before attachment kinds still validate; clients
   * fall back to `attachmentKindForMime(mimeType)`.
   */
  kind: aiAttachmentKindSchema.optional(),
  /** Characters of text extracted; 0 means a scanned PDF or image with no readable text. */
  textChars: z.number().int().nonnegative(),
  /** Spreadsheets only: data rows parsed (header excluded), summed over sheets read. */
  rowCount: z.number().int().nonnegative().optional(),
  /** Spreadsheets only: the sheet names read (a CSV has one). */
  sheetNames: z.array(z.string()).optional(),
  createdAt: instantSchema,
});
export type AiAttachment = z.infer<typeof aiAttachmentSchema>;

// ---------------------------------------------------------------------------
// Allowances & usage
// ---------------------------------------------------------------------------

export const AI_PERIOD_KINDS = ['month', 'year'] as const;
export const aiPeriodKindSchema = z.enum(AI_PERIOD_KINDS);

export const aiAllowanceSchema = z.object({
  period: z.object({ kind: aiPeriodKindSchema, start: instantSchema, end: instantSchema }),
  includedTokens: z.number().int().nonnegative(),
  addOnTokens: z.number().int().nonnegative(),
  consumedTokens: z.number().int().nonnegative(),
  remainingTokens: z.number().int().nonnegative(),
  warnAtPercent: z.number().int().min(1).max(100),
  /** false = warn only; the org is billed on usage past the allowance. */
  hardStop: z.boolean(),
  exhausted: z.boolean(),
});
export type AiAllowance = z.infer<typeof aiAllowanceSchema>;

export const aiAddOnSchema = z.object({
  id: uuidSchema,
  tokens: z.number().int().positive(),
  remainingTokens: z.number().int().nonnegative(),
  purchasedAt: instantSchema,
  expiresAt: instantSchema.nullable(),
  note: z.string().nullable(),
});
export type AiAddOn = z.infer<typeof aiAddOnSchema>;

export const aiUsageRowSchema = z.object({
  /** ISO date for `groupBy=day`, a person id for `groupBy=person`. */
  key: z.string(),
  label: z.string().nullable(),
  turns: z.number().int().nonnegative(),
  promptTokens: z.number().int().nonnegative(),
  completionTokens: z.number().int().nonnegative(),
});

// ---------------------------------------------------------------------------
// Streaming events (Server-Sent Events on `sendMessage`)
// ---------------------------------------------------------------------------

export const aiSseEventSchema = z.discriminatedUnion('event', [
  z.object({ event: z.literal('turn'), data: z.object({ turnId: uuidSchema }) }),
  z.object({ event: z.literal('delta'), data: z.object({ text: z.string() }) }),
  z.object({
    event: z.literal('tool'),
    data: z.object({
      id: z.string(),
      name: z.string(),
      status: aiToolCallStatusSchema,
      summary: z.string().max(200).optional(),
    }),
  }),
  z.object({ event: z.literal('proposal'), data: aiProposalSchema }),
  z.object({ event: z.literal('navigate'), data: aiNavigationSchema }),
  z.object({ event: z.literal('question'), data: aiQuestionSchema }),
  z.object({
    event: z.literal('done'),
    data: z.object({
      turn: aiTurnSchema,
      message: aiMessageSchema,
      proposals: z.array(aiProposalSchema),
    }),
  }),
  z.object({
    event: z.literal('error'),
    data: z.object({ code: z.string(), message: z.string() }),
  }),
]);
export type AiSseEvent = z.infer<typeof aiSseEventSchema>;

// ---------------------------------------------------------------------------
// Request bodies
// ---------------------------------------------------------------------------

export const createConversationBodySchema = z.object({ effort: aiEffortSchema.optional() });

export const sendMessageBodySchema = z.object({
  content: z.string().trim().min(1).max(8000),
  attachmentIds: z.array(uuidSchema).max(AI_ATTACHMENT_MAX_PER_MESSAGE).optional(),
  /** Client-generated; a retried send with the same id is answered from the first turn. */
  clientMessageId: uuidSchema,
  context: z
    .object({
      /** The screen the person is on, so "this employee" can resolve. Validated as a path. */
      route: z
        .string()
        .max(500)
        .regex(/^\/(?!\/)[^\s]*$/)
        .optional(),
    })
    .optional(),
});
export type SendMessageBody = z.infer<typeof sendMessageBodySchema>;

export const sendMessageResponseSchema = z.object({
  turn: aiTurnSchema,
  messages: z.array(aiMessageSchema),
  proposals: z.array(aiProposalSchema),
});
export type SendMessageResponse = z.infer<typeof sendMessageResponseSchema>;

export const listProposalsQuerySchema = paginationQuerySchema.extend({
  status: aiProposalStatusSchema.optional(),
});

export const requestAddOnBodySchema = z.object({
  tokens: z.number().int().positive().max(1_000_000_000),
  note: z.string().trim().max(1000).optional(),
});

export const listUsageQuerySchema = z.object({
  from: instantSchema.optional(),
  to: instantSchema.optional(),
  groupBy: z.enum(['day', 'person']).default('day'),
});

// ---------------------------------------------------------------------------
// Routes
// ---------------------------------------------------------------------------

export const aiContract = c.router(
  {
    listConversations: {
      method: 'GET',
      path: '/conversations',
      query: paginationQuerySchema,
      responses: {
        200: paginatedSchema(aiConversationSchema),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: "List the caller's copilot conversations, most recent first",
      metadata: routeMeta('ai.copilot:use'),
    },
    createConversation: {
      method: 'POST',
      path: '/conversations',
      body: createConversationBodySchema,
      responses: {
        201: aiConversationSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        503: errorBodySchema,
      },
      summary: 'Start a conversation; effort and model are fixed for its lifetime',
      metadata: routeMeta('ai.copilot:use'),
    },
    getConversation: {
      method: 'GET',
      path: '/conversations/:conversationId',
      responses: {
        200: z.object({
          conversation: aiConversationSchema,
          messages: z.array(aiMessageSchema),
          proposals: z.array(aiProposalSchema),
        }),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'A conversation with its messages and every proposal it produced',
      metadata: routeMeta('ai.copilot:use'),
    },
    sendMessage: {
      method: 'POST',
      path: '/conversations/:conversationId/messages',
      body: sendMessageBodySchema,
      responses: {
        200: sendMessageResponseSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        422: errorBodySchema,
        429: errorBodySchema,
        503: errorBodySchema,
      },
      summary:
        'Send a message and run one model turn. JSON by default; SSE with Accept: text/event-stream',
      metadata: routeMeta('ai.copilot:use'),
    },
    getTurn: {
      method: 'GET',
      path: '/turns/:turnId',
      responses: {
        200: z.object({
          turn: aiTurnSchema,
          message: aiMessageSchema.nullable(),
          proposals: z.array(aiProposalSchema),
        }),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Catch up on a turn after a dropped stream',
      metadata: routeMeta('ai.copilot:use'),
    },
    listProposals: {
      method: 'GET',
      path: '/proposals',
      query: listProposalsQuerySchema,
      responses: {
        200: paginatedSchema(aiProposalSchema),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: "The caller's proposals, newest first, optionally by status",
      metadata: routeMeta('ai.copilot:use'),
    },
    approveProposal: {
      method: 'POST',
      path: '/proposals/:proposalId/approve',
      body: c.noBody(),
      responses: {
        200: z.object({ proposal: aiProposalSchema }),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        410: errorBodySchema,
      },
      summary:
        'Execute a pending proposal as the caller. Own proposal, own session, once; 409 if already decided, 410 if expired',
      metadata: routeMeta('ai.agent.write:execute'),
    },
    rejectProposal: {
      method: 'POST',
      path: '/proposals/:proposalId/reject',
      body: c.noBody(),
      responses: {
        200: z.object({ proposal: aiProposalSchema }),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Decline a pending proposal',
      metadata: routeMeta('ai.agent.write:execute'),
    },
    getAllowance: {
      method: 'GET',
      path: '/allowance',
      responses: { 200: aiAllowanceSchema, 401: errorBodySchema, 403: errorBodySchema },
      summary: "The org's AI allowance for the current period — what the dock's meter shows",
      metadata: routeMeta('ai.copilot:use'),
    },
    listAddOns: {
      method: 'GET',
      path: '/add-ons',
      responses: {
        200: z.object({ items: z.array(aiAddOnSchema) }),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'Purchased token packs and their remaining balance',
      metadata: routeMeta(['ai.settings:manage', 'org.billing:read']),
    },
    requestAddOn: {
      method: 'POST',
      path: '/add-ons/request',
      body: requestAddOnBodySchema,
      responses: {
        201: z.object({ requestId: uuidSchema }),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'Ask for an add-on pack; sales is notified and the pack is granted out of band',
      metadata: routeMeta(['ai.settings:manage', 'org.billing:update']),
    },
    listUsage: {
      method: 'GET',
      path: '/usage',
      query: listUsageQuerySchema,
      responses: {
        200: z.object({ rows: z.array(aiUsageRowSchema) }),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'Token usage by day or by person, from the turn ledger',
      metadata: routeMeta(['ai.settings:manage', 'org.billing:read', 'ai.audit:read']),
    },
  },
  { pathPrefix: '/ai' },
);
